import type { TFunction } from "i18next";

/**
 * Localized "time until X" formatter for the webinar promo surfaces.
 *
 * Rules (per the product spec):
 *   - ≥ 1 day  → days only          ("בעוד 3 ימים")
 *   - < 1 day  → hours only         ("בעוד 5 שעות")
 *   - < 3 hours → hours + minutes   ("בעוד שעתיים, 4 דקות")
 *   - < 1 hour → minutes only       ("בעוד 23 דקות")
 *
 * Returns null when the target is in the past — callers should hide the
 * countdown rather than show "0 minutes".
 *
 * Plural handling is hand-rolled (one/two/other) so we don't depend on
 * i18next's plural inflection being configured for Hebrew. Each locale
 * fills the right form in its own JSON file.
 */
export function formatWebinarCountdown(
  targetMs: number,
  nowMs: number,
  t: TFunction
): string | null {
  const diffMs = targetMs - nowMs;
  if (diffMs <= 0) return null;

  const totalMinutes = Math.floor(diffMs / (60 * 1000));
  const totalHours = Math.floor(diffMs / (60 * 60 * 1000));
  const totalDays = Math.floor(diffMs / (24 * 60 * 60 * 1000));

  const prefix = t("dashboardPage.widget.webinarPromo.countdown.prefix", "in ");
  const separator = t("dashboardPage.widget.webinarPromo.countdown.separator", ", ");

  // ≥ 1 day → days only.
  if (totalDays >= 1) {
    return prefix + pluralizeUnit(totalDays, t, "day");
  }

  // < 3 hours → hours + minutes (only if minutes > 0; otherwise just hours).
  if (totalHours >= 1 && totalHours < 3) {
    const minutesPart = totalMinutes - totalHours * 60;
    const hoursStr = pluralizeUnit(totalHours, t, "hour");
    if (minutesPart > 0) {
      const minutesStr = pluralizeUnit(minutesPart, t, "minute");
      return prefix + hoursStr + separator + minutesStr;
    }
    return prefix + hoursStr;
  }

  // 3+ hours up to a day → hours only.
  if (totalHours >= 3) {
    return prefix + pluralizeUnit(totalHours, t, "hour");
  }

  // < 1 hour → minutes. Floor to at least 1 so we never render "0 minutes".
  return prefix + pluralizeUnit(Math.max(1, totalMinutes), t, "minute");
}

type Unit = "day" | "hour" | "minute";

function pluralizeUnit(count: number, t: TFunction, unit: Unit): string {
  const base = `dashboardPage.widget.webinarPromo.countdown.${unit}`;
  // Hebrew has a dual form ("יומיים" / "שעתיים"). English skips _two and
  // falls through to _other; that's fine because the en.json `_other`
  // string covers both (e.g. "2 hours"). Minutes don't get a _two on
  // purpose — modern Hebrew typically says "2 דקות" rather than "דקתיים".
  if (count === 1) return t(`${base}_one`, { count });
  if (count === 2 && unit !== "minute") return t(`${base}_two`, { count });
  return t(`${base}_other`, { count });
}
